import React, { Suspense, useContext, useEffect } from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { StyleSheetManager, ThemeProvider as StyledThemeProvider } from 'styled-components'
import LoadingContext from './LoadingContext'

// Brand palette sampled from the logo assets (headline.png / logo512.png):
// the green K mark #5bb645 and the wordmark near-black #1b1b1b. The primary
// is a darker shade of the logo hue so the logo itself stays visible against
// primary-colored surfaces (the AppBar); the logo green serves as `light`.
const BRAND_GREEN = '#5bb645'
const BRAND_GREEN_DARK = '#3b762d'
const BRAND_BLACK = '#1b1b1b'
// Matches the light-gray AppBar (components/Header/styled.js) so the android
// status bar blends with the bar below it.
const APP_BAR_GRAY = '#f2f4f6'

const theme = createTheme({
  direction: 'rtl', // Both here and <body dir="rtl">
  palette: {
    primary: {
      main: BRAND_GREEN_DARK,
      light: BRAND_GREEN,
      contrastText: '#ffffff',
    },
    secondary: {
      main: BRAND_BLACK,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          direction: 'rtl',
          textAlign: 'right',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        input: {
          direction: 'rtl',
          textAlign: 'right !important',
        },
      },
    },
    MuiNativeSelect: {
      styleOverrides: {
        select: {
          paddingRight: '0 !important',
          paddingLeft: '24px !important',
        },
        icon: {
          right: 'auto !important',
          left: '0 !important',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          right: 'auto !important',
          left: '7px !important',
        },
        select: {
          paddingRight: '14px !important',
          paddingLeft: '32px !important',
        },
      },
    },
    MuiTableCell: {
      defaultProps: {
        align: 'right',
      },
      styleOverrides: {
        root: {
          textAlign: 'right',
        },
        head: {
          textAlign: 'right',
        },
        body: {
          textAlign: 'right',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          direction: 'rtl',
          textAlign: 'right',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          direction: 'rtl',
          textAlign: 'right',
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          direction: 'rtl',
          textAlign: 'right',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          direction: 'rtl',
          // #2731: MUI spaces the buttons with a one-sided `marginLeft` on every child
          // but the first. Under `direction: rtl` that margin lands on the OUTER edge
          // of the second button, so the two touch. `gap` is direction-agnostic.
          gap: 8,
          '& > :not(:first-of-type)': { marginLeft: 0 },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          direction: 'ltr',        // reset inherited RTL to prevent double-flip
          flexDirection: 'row-reverse', // icon on right, action on left
        },
        icon: {
          marginRight: 0,
          marginLeft: '12px',      // gap between icon (right) and message
        },
        message: {
          textAlign: 'right',
          direction: 'rtl',
        },
        action: {
          paddingLeft: 0,
          paddingRight: '16px',    // gap between action (left) and message
          marginLeft: 0,           // override MUI default 'auto' to keep action at left edge
          marginRight: '-8px',
        },
      },
    },
  },
})

function ThemeRoot({ children }) {
  useEffect(() => {
    // Re-apply after CssBaseline mounts (it sets color-scheme:light which
    // causes Chrome Android to reset the status bar color to white)
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute("content", APP_BAR_GRAY)
  }, [])

  return (
    <Suspense fallback={<Loader/>}>
      <ThemeProvider {...{ theme }}>
        <CssBaseline />
        <StyledThemeProvider {...{ theme }}>
          <RTL>
            {children}
          </RTL>
        </StyledThemeProvider>
      </ThemeProvider>
    </Suspense>
  )
}

export default ThemeRoot

const RTL = ({ children }) => (
  <StyleSheetManager>
    <div dir="rtl" style={{ height: '100%' }}>
      {children}
    </div>
  </StyleSheetManager>
)

const Loader = () => {
  const loading = useContext(LoadingContext)

  useEffect(() => {
    loading.open()
    
    return () => loading.close()
  })

  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <div style={{ marginBottom: '10px', fontSize: '16px' }}>טוען נתונים מהשרת...</div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '4px',
        marginTop: '15px' 
      }}>
        {[0, 1, 2].map(i => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: BRAND_GREEN,
              animation: `pulse 1s ease-in-out ${i * 0.2}s infinite`
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  )
}
